import type { z } from 'zod'

export type ApiBoundaryIssue = {
  entity: string
  issues: z.core.$ZodIssue[]
}

export type ApiBoundaryReporter = (issue: ApiBoundaryIssue) => void

/** lenient 로 넘어간 어긋남을 콘솔에서 읽을 수 있게 찍을 때 쓰는 접두어. e2e 가 이 줄만 따로 모은다. */
export const API_BOUNDARY_LOG_PREFIX = '[schema] '

type ApiBoundaryConfig = {
  strict: boolean
  reporter: ApiBoundaryReporter
}

// 기본은 strict(예외를 던짐)다. 앱은 흰 화면을 피하려고 부트스트랩에서 모든 환경을 lenient 로 낮추므로
// strict 는 앱 설정을 거치지 않는 단위 테스트에서만 쓰인다.
const config: ApiBoundaryConfig = {
  strict: true,
  reporter: () => {},
}

export const configureApiBoundary = (next: Partial<ApiBoundaryConfig>) => {
  Object.assign(config, next)
}

export const parseApiResponse = <S extends z.ZodType>(
  schema: S,
  raw: unknown,
  entity: string,
): z.output<S> => {
  if (config.strict) return schema.parse(raw)
  const result = schema.safeParse(raw)
  if (result.success) return result.data
  config.reporter({ entity, issues: result.error.issues })
  // 어느 필드가 어긋났는지 콘솔에서 바로 읽히게 남긴다. 운영에서도 보고는 reporter 가 맡고
  // 이 줄은 개발자 도구를 열어 둔 사람에게만 의미가 있다.
  const detail = result.error.issues
    .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join(' | ')
  // eslint-disable-next-line no-console
  console.warn(`${API_BOUNDARY_LOG_PREFIX}${entity} 응답이 스키마와 다릅니다 — ${detail}`)
  return raw as z.output<S>
}
